<template>
  <!-- eslint-disable vue/no-v-html -->
  <div
    v-if="isEditMode && (field?.editable || fieldValue?.editable)"
    v-html="field?.editable || fieldValue?.editable"
  />
  <!-- eslint-enable vue/no-v-html -->
  <picture
    v-else-if="imgSrc"
    :class="{
      'ssr-cloud-image-fi': true,
      'ssr-cloud-image-fi--inline-edit': inlineEditMode && edit !== null,
    }"
    @click="onClickImage"
  >
    <source
      v-for="breakpoint in breakpoints"
      :key="breakpoint.mediaWidth"
      :media="`(max-width: ${breakpoint.mediaWidth}px)`"
      :srcset="formatSrcSet(imgSrc, breakpoint.imageWidth, breakpoint.imageHeight)"
    />
    <img
      :loading="eager ? undefined : 'lazy'"
      :fetchpriority="eager ? 'high' : undefined"
      :src="baseSrc"
      :srcset="formatSrcSet(imgSrc, unlimited, unlimitedHeight)"
      :alt="altPropOrField"
      :width="field?.value?.width || unlimited"
      :height="
        field?.value?.height ||
        unlimitedHeight ||
        (aspectRatio ? asFloat(unlimited) / aspectRatio : undefined)
      "
      :title="titlePropOrField"
    />
  </picture>
</template>

<script setup lang="ts">
import { computed, onMounted, type Ref, ref } from 'vue';

import { useCloudImage } from '@/composables/cloudImage';
import { useInlineEdit } from '@/composables/inlineEdit';
import type { ImageField } from '@/sitecoreFieldTypes.d.ts';

export interface Breakpoint {
  mediaWidth: number;
  imageWidth: number;
  imageHeight?: number;
}

export type BreakPoints = Array<Breakpoint>;

defineOptions({
  name: 'SsrCloudImageFi',
});

const { appendParams, getProxySrc } = useCloudImage();

const props = withDefaults(
  defineProps<{
    field?: ImageField;
    src?: string;
    params?: string;
    trim?: boolean;
    alt?: string;
    title?: string;
    breakpoints: BreakPoints;
    unlimited?: string | number;
    unlimitedHeight?: string | number;
    width?: string | number;
    height?: string | number;
    aspectRatio?: number;
    eager?: boolean;
    fieldName?: string | null | undefined;
  }>(),
  {
    unlimited: '2000',
    field: undefined,
    src: undefined,
    params: undefined,
    alt: undefined,
    title: undefined,
    unlimitedHeight: undefined,
    width: undefined,
    height: undefined,
    aspectRatio: undefined,
    fieldName: undefined,
  },
);

const {
  inlineEditMode,
  fieldValue: fieldValueMisCast,
  offerEditMode,
  notifyChanges,
  openMediaBrowser,
} = useInlineEdit(props.fieldName);
const fieldValue = fieldValueMisCast as Ref<ImageField>;

const edit = ref<string | null>(null);

const onClickImage = async () => {
  if (!inlineEditMode.value || edit.value === null) return;
  const value = await openMediaBrowser();
  if (value !== null) {
    edit.value = value.ItemMedialUrl.replace(/[&?]thn=1(?:&|$)/, '');
    notifyChanges(true, `<image mediaid="{${value.ItemID}}" />`);
  }
};

onMounted(() => {
  if (fieldValue.value && !fieldValue.value.editable && offerEditMode.value) {
    edit.value = fieldValue.value.value?.src || null;
  }
});

const imgSrc = computed<string>(() => {
  let src = edit.value || props.src || props.field?.value?.src || fieldValue.value?.value?.src;
  if (!src) {
    return '';
  }

  // NOTE: attempting to do both at once ...[hw]=... does not work, since it won't match the replacement
  src = src.replace(/([?&])w=[0-9]+(?:&|$)/, (_: string, separator: string) => separator);
  src = src.replace(/([?&])h=[0-9]+(?:&|$)/, (_: string, separator: string) => separator);

  const params = [];
  if (props.trim) {
    params.push('trim=5');
  }

  if (props.params) {
    params.push(props.params);
  }

  if (inlineEditMode.value) {
    params.push('inlineEdit=true');
  }

  return appendParams(`${getProxySrc(src)}`, params.join('&'));
});

const asFloat = (input: string | number): number =>
  typeof input === 'number' ? input : Number.parseFloat(input);

const formatWidthHeight = (
  url: Array<string>,
  factor: number,
  imageWidth: number | string,
  imageHeight: number | string | undefined,
): void => {
  const factoredImageWidth = factor * asFloat(imageWidth);
  url.push('&w=', String(factoredImageWidth));
  if (imageHeight) {
    url.push('&h=', String(factor * asFloat(imageHeight)));
  } else if (props.aspectRatio) {
    url.push('&h=', String(factoredImageWidth / props.aspectRatio));
  }
};

const baseSrc = computed<string>(() => {
  const url = [imgSrc.value];
  formatWidthHeight(url, 1, props.unlimited, props.unlimitedHeight);
  return url.join('');
});

const altPropOrField = computed<string | undefined>(
  () => props.alt || props.field?.value?.alt || fieldValue.value?.value?.alt,
);

const titlePropOrField = computed<string | undefined>(
  () => props.title || props.field?.value?.title || fieldValue.value?.value?.title,
);

const formatSrcSet = (
  src: string,
  imageWidth: string | number,
  imageHeight: string | number | undefined,
): string => {
  const url: Array<string> = [];
  [1, 1.5, 2, 3, 4].forEach((factor) => {
    url.push(src);

    formatWidthHeight(url, factor, imageWidth, imageHeight);

    url.push(' ', String(factor), 'x');
    if (factor !== 4) {
      url.push(',');
    }
  });

  return url.join('');
};
</script>

<style scoped src="./ssr-cloud-image-fi" />
